import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Plus, X, ChevronUp, ChevronDown,
  Edit2, Trash2, Filter, RefreshCw, Users,
  AlertCircle, Upload, Download
} from 'lucide-react';
import API_BASE_URL from '../config';
import Swal from 'sweetalert2';

const CUSTOMERS_API = `${API_BASE_URL}api/Customer_marketing`;

const emptyForm = {
  id: 0,
  bpCode: '',
  bpName: '',
  groupCode: '',
  salesEmployeeCode: '',
  taxGroup: '',
  subjectToWithholding: false,
  shipToDefault: '',
};

const CSV_HEADERS = [
  'id', 'bpCode', 'bpName', 'groupCode',
  'salesEmployeeCode', 'taxGroup', 'subjectToWithholding', 'shipToDefault'
];

const CustomerMaintenance = () => {
  const [customers, setCustomers]             = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [showModal, setShowModal]             = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingItem, setEditingItem]         = useState(null);
  const [formData, setFormData]               = useState(emptyForm);
  const [searchTerm, setSearchTerm]           = useState('');
  const [filterField, setFilterField]         = useState('all');
  const [sortField, setSortField]             = useState('id');
  const [sortDirection, setSortDirection]     = useState('asc');
  const [currentPage, setCurrentPage]         = useState(1);
  const [importData, setImportData]           = useState([]);
  const [duplicates, setDuplicates]           = useState([]);
  const fileInputRef = useRef(null);
  const itemsPerPage = 10;

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await fetch(CUSTOMERS_API);
      if (res.ok) setCustomers(await res.json());
    } catch (err) { console.error('Fetch error:', err); }
    setLoading(false);
  };

  useEffect(() => { fetchCustomers(); }, []);

  // ── Create ─────────────────────────────────────────────────────────────────
  const createCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(CUSTOMERS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', accept: 'text/plain' },
        body: JSON.stringify(formData),
      });
      if (res.status === 409) {
        const err = await res.json();
        Swal.fire({ icon: 'warning', title: 'Duplicate BP Code', text: err.message, confirmButtonColor: '#185FA5' });
      } else if (res.ok) {
        closeModal(); fetchCustomers();
        Swal.fire({ icon: 'success', title: 'Created!', timer: 1500, showConfirmButton: false });
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // ── Update ─────────────────────────────────────────────────────────────────
  const updateCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${CUSTOMERS_API}/${editingItem.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', accept: 'text/plain' },
        body: JSON.stringify(formData),
      });
      if (res.status === 409) {
        const err = await res.json();
        Swal.fire({ icon: 'warning', title: 'Duplicate BP Code', text: err.message, confirmButtonColor: '#185FA5' });
      } else if (res.ok || res.status === 204) {
        closeModal(); fetchCustomers();
        Swal.fire({ icon: 'success', title: 'Updated!', timer: 1500, showConfirmButton: false });
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // ── Delete single ──────────────────────────────────────────────────────────
  const deleteCustomer = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Customer?', text: 'This action cannot be undone!', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#E24B4A', cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!',
    });
    if (!result.isConfirmed) return;
    setLoading(true);
    try {
      const res = await fetch(`${CUSTOMERS_API}/${id}`, { method: 'DELETE' });
      if (res.ok || res.status === 204) {
        setCustomers(prev => prev.filter(c => c.id !== id));
        Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1200, showConfirmButton: false });
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // ── Delete all ─────────────────────────────────────────────────────────────
  const deleteAllCustomers = async () => {
    if (customers.length === 0) {
      return Swal.fire({ icon: 'info', title: 'No Customers', text: 'Nothing to delete!', confirmButtonColor: '#185FA5' });
    }
    const first = await Swal.fire({
      title: 'Delete All Customers?',
      html: `You are about to delete <strong>${customers.length}</strong> customers.<br/>This cannot be undone!`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#E24B4A', cancelButtonColor: '#64748b', confirmButtonText: 'Yes, delete all!',
    });
    if (!first.isConfirmed) return;
    const second = await Swal.fire({
      title: 'Final Warning!', text: 'Permanently delete ALL customers?', icon: 'error',
      showCancelButton: true, confirmButtonColor: '#E24B4A', cancelButtonColor: '#64748b', confirmButtonText: 'DELETE ALL',
    });
    if (!second.isConfirmed) return;

    setLoading(true);
    const total = customers.length; let done = 0;
    Swal.fire({
      title: 'Deleting Customers',
      html: counter(done, total, '#E24B4A'),
      allowOutsideClick: false, didOpen: () => Swal.showLoading(),
    });
    let success = 0, fail = 0;
    for (const c of customers) {
      try {
        const res = await fetch(`${CUSTOMERS_API}/${c.id}`, { method: 'DELETE' });
        res.ok || res.status === 204 ? success++ : fail++;
      } catch { fail++; }
      done++;
      Swal.update({ html: counter(done, total, '#E24B4A') });
    }
    await fetchCustomers();
    Swal.fire({
      icon: success === total ? 'success' : 'warning', title: 'Done',
      html: `✅ Deleted: <strong>${success}</strong>${fail > 0 ? `<br/>❌ Failed: <strong>${fail}</strong>` : ''}`,
      confirmButtonColor: '#185FA5',
    });
    setLoading(false);
  };

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (customers.length === 0) return Swal.fire({ icon: 'info', title: 'No Data', text: 'No customers to export!', confirmButtonColor: '#185FA5' });
    const csvContent = [
      CSV_HEADERS.join(','),
      ...customers.map(c => CSV_HEADERS.map(h => `"${c[h] ?? ''}"`).join(','))
    ].join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }));
    link.download = `customers_export_${Date.now()}.csv`;
    link.click();
  };

  // ── Import: File Select ────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n').filter(l => l.trim());
      if (lines.length < 2) return Swal.fire({ icon: 'error', title: 'Invalid File', text: 'CSV must have header + at least 1 row.' });
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const parsed = lines.slice(1).map(line => {
        const vals = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const obj = {};
        headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
        return obj;
      });
      checkDuplicates(parsed);
      setShowImportModal(true);
    };
    reader.readAsText(file);
  };

  // ── Import: Check Duplicates ───────────────────────────────────────────────
  const checkDuplicates = (rows) => {
    const dupes = [], nonDupes = [];
    rows.forEach(row => {
      const existing = customers.find(c => c.bpCode?.toLowerCase() === row.bpCode?.toLowerCase());
      if (existing) {
        dupes.push({ ...row, existingId: existing.id, existing, action: 'skip' });
      } else {
        nonDupes.push(row);
      }
    });
    setImportData(nonDupes);
    setDuplicates(dupes);
  };

  const handleDuplicateAction = (index, action) => {
    setDuplicates(prev => { const u = [...prev]; u[index].action = action; return u; });
  };

  // ── Import: Process ────────────────────────────────────────────────────────
  const processImport = async () => {
    const toUpdate = duplicates.filter(d => d.action === 'update');
    const total = importData.length + toUpdate.length;
    let done = 0;
    setLoading(true);

    Swal.fire({
      title: 'Importing Customers',
      html: counter(done, total, '#185FA5'),
      allowOutsideClick: false, didOpen: () => Swal.showLoading(),
    });

    try {
      for (const row of importData) {
        await fetch(CUSTOMERS_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', accept: 'text/plain' },
          body: JSON.stringify({
            id: 0,
            bpCode: row.bpCode || '',
            bpName: row.bpName || '',
            groupCode: row.groupCode || '',
            salesEmployeeCode: row.salesEmployeeCode || '',
            taxGroup: row.taxGroup || '',
            subjectToWithholding: row.subjectToWithholding === 'true' || row.subjectToWithholding === true,
            shipToDefault: row.shipToDefault || '',
          }),
        });
        done++;
        Swal.update({ html: counter(done, total, '#185FA5') });
      }

      for (const dup of toUpdate) {
        await fetch(`${CUSTOMERS_API}/${dup.existingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', accept: 'text/plain' },
          body: JSON.stringify({
            id: dup.existingId,
            bpCode: dup.bpCode || '',
            bpName: dup.bpName || '',
            groupCode: dup.groupCode || '',
            salesEmployeeCode: dup.salesEmployeeCode || '',
            taxGroup: dup.taxGroup || '',
            subjectToWithholding: dup.subjectToWithholding === 'true' || dup.subjectToWithholding === true,
            shipToDefault: dup.shipToDefault || '',
          }),
        });
        done++;
        Swal.update({ html: counter(done, total, '#185FA5') });
      }

      await fetchCustomers();
      closeImportModal();
      Swal.fire({
        icon: 'success', title: 'Import Complete!',
        html: `✅ Created: <strong>${importData.length}</strong><br/>🔄 Updated: <strong>${toUpdate.length}</strong><br/>⏭ Skipped: <strong>${duplicates.filter(d => d.action === 'skip').length}</strong>`,
        confirmButtonColor: '#185FA5',
      });
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Import Failed', text: 'An error occurred.', confirmButtonColor: '#E24B4A' });
    }
    setLoading(false);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportData([]);
    setDuplicates([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openCreateModal = () => { setEditingItem(null); setFormData(emptyForm); setShowModal(true); };
  const openEditModal   = (item) => { setEditingItem(item); setFormData({ ...item }); setShowModal(true); };
  const closeModal      = () => { setShowModal(false); setEditingItem(null); setFormData(emptyForm); };
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // ── Filter / Sort / Paginate ───────────────────────────────────────────────
  const filtered = customers.filter(c => {
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    if (filterField === 'all') return (
      c.bpCode?.toLowerCase().includes(q) ||
      c.bpName?.toLowerCase().includes(q) ||
      c.groupCode?.toLowerCase().includes(q) ||
      c.salesEmployeeCode?.toLowerCase().includes(q) ||
      c.taxGroup?.toLowerCase().includes(q) ||
      c.shipToDefault?.toLowerCase().includes(q)
    );
    return c[filterField]?.toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortField] ?? '', bv = b[sortField] ?? '';
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    return sortDirection === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const totalPages = Math.ceil(sorted.length / itemsPerPage);
  const paginated  = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="opacity-30" size={14} />;
    return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const counter = (done, total, color) =>
    `<div style="text-align:center"><div style="font-size:48px;font-weight:bold;color:${color};margin:20px 0">${done}/${total}</div><div style="color:#64748b">Processing...</div></div>`;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: '#f1f5f9',
      padding: '2rem 1.5rem',
      fontFamily: '"Inter", -apple-system, sans-serif',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }

        /* ── Card ── */
        .glass-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }

        /* ── Buttons ── */
        .btn { height: 38px; padding: 0 16px; border-radius: 8px; border: none; font-weight: 500; font-size: 13px; cursor: pointer; transition: all .2s; display: inline-flex; align-items: center; gap: 6px; font-family: 'Inter', sans-serif; white-space: nowrap; }
        .btn:hover  { opacity: .88; transform: translateY(-1px); }
        .btn:active { transform: scale(.97); }
        .btn:disabled { opacity: .45; cursor: not-allowed; transform: none; }

        .btn-primary  { background: #185FA5; color: #fff; }
        .btn-success  { background: #1D9E75; color: #fff; }
        .btn-warning  { background: #BA7517; color: #fff; }
        .btn-danger   { background: #E24B4A; color: #fff; }
        .btn-ghost    { background: #fff; color: #475569; border: 1px solid #e2e8f0; }
        .btn-ghost:hover { background: #f8fafc; }

        /* ── Inputs ── */
        .input-field { width: 100%; height: 38px; padding: 0 12px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; color: #1e293b; font-size: 14px; font-family: 'Inter', sans-serif; transition: border-color .2s, box-shadow .2s; }
        .input-field:focus { outline: none; border-color: #185FA5; box-shadow: 0 0 0 3px rgba(24,95,165,.1); }
        .input-field::placeholder { color: #94a3b8; }

        /* ── Select ── */
        .select-field { height: 38px; padding: 0 32px 0 36px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; color: #1e293b; font-size: 14px; font-family: 'Inter', sans-serif; cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 9L1 4h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; background-color: #fff; transition: border-color .2s, box-shadow .2s; }
        .select-field:focus { outline: none; border-color: #185FA5; box-shadow: 0 0 0 3px rgba(24,95,165,.1); }

        /* ── Icon wrap ── */
        .icon-wrap { position: relative; }
        .icon-wrap .icon-left { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; display: flex; }

        /* ── Controls row ── */
        .controls-grid { display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center; }
        .controls-left { display: grid; grid-template-columns: 240px auto; gap: 8px; align-items: center; }
        .controls-right { display: flex; gap: 6px; align-items: center; }
        .btn-divider { width: 1px; height: 24px; background: #e2e8f0; margin: 0 2px; }

        /* ── Badges ── */
        .stat-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 99px; font-size: 12px; color: #64748b; font-weight: 500; }
        .stat-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

        /* ── Table ── */
        .table-container { overflow-x: auto; border-radius: 12px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #f8fafc; padding: .875rem 1rem; text-align: left; font-weight: 600; color: #64748b; font-size: .78rem; text-transform: uppercase; letter-spacing: .06em; cursor: pointer; user-select: none; border-bottom: 1px solid #e2e8f0; white-space: nowrap; transition: background .15s; }
        th:hover { background: #f1f5f9; }
        td { padding: .875rem 1rem; border-bottom: 1px solid #f1f5f9; color: #334155; font-size: .875rem; }
        tr:hover td { background: #fafbfd; }
        tr:last-child td { border-bottom: none; }
        .th-inner { display: flex; align-items: center; gap: 4px; }

        /* ── Modal ── */
        .modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.45); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 1000; animation: fadeIn .2s ease; }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        .modal-content { animation: slideUp .25s cubic-bezier(.4,0,.2,1); width: min(520px, 92vw); max-height: 92vh; overflow-y: auto; }
        .modal-content-lg { animation: slideUp .25s cubic-bezier(.4,0,.2,1); width: min(860px, 95vw); max-height: 92vh; overflow-y: auto; }
        @keyframes slideUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }

        /* ── Form ── */
        .form-label { display: block; margin-bottom: 6px; color: #475569; font-weight: 500; font-size: .85rem; }
        .form-group { display: flex; flex-direction: column; }
        .checkbox-row { display: flex; align-items: center; gap: 10px; padding: 10px 0; }
        .checkbox-row input[type="checkbox"] { width: 16px; height: 16px; accent-color: #185FA5; cursor: pointer; }

        /* ── Pagination ── */
        .pagination { display: flex; gap: 4px; align-items: center; justify-content: center; flex-wrap: wrap; }
        .page-btn { height: 34px; min-width: 34px; padding: 0 10px; background: #fff; border: 1px solid #e2e8f0; border-radius: 7px; color: #475569; cursor: pointer; transition: all .15s; font-size: .875rem; font-family: 'Inter', sans-serif; }
        .page-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #185FA5; }
        .page-btn.active { background: #185FA5; border-color: #185FA5; color: #fff; font-weight: 600; }
        .page-btn:disabled { opacity: .4; cursor: not-allowed; }
        .page-ellipsis { color: #94a3b8; padding: 0 4px; line-height: 34px; }

        /* ── Spinner ── */
        .spinner { border: 2px solid #e2e8f0; border-top: 2px solid #185FA5; border-radius: 50%; animation: spin .7s linear infinite; flex-shrink: 0; }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }

        /* ── Icon button ── */
        .icon-btn { width: 32px; height: 32px; background: transparent; border: none; cursor: pointer; border-radius: 7px; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; color: #94a3b8; }
        .icon-btn.edit:hover  { background: #eff6ff; color: #185FA5; }
        .icon-btn.delete:hover { background: #fef2f2; color: #E24B4A; }

        /* ── Duplicate card ── */
        .dup-card { background: #fffbeb; border: 1px solid #fde68a; padding: 1rem; border-radius: 10px; }
        .act-btn { height: 30px; padding: 0 12px; border-radius: 6px; border: none; font-size: .8rem; cursor: pointer; transition: all .15s; font-family: 'Inter', sans-serif; font-weight: 500; }
        .act-skip   { background: #f1f5f9; color: #64748b; }
        .act-skip.on   { background: #94a3b8; color: #fff; }
        .act-update { background: #dbeafe; color: #1e40af; }
        .act-update.on { background: #185FA5; color: #fff; }

        /* ── Withholding badge ── */
        .badge-yes { padding: 3px 10px; border-radius: 99px; font-size: .78rem; font-weight: 600; background: #d1fae5; color: #065f46; }
        .badge-no  { padding: 3px 10px; border-radius: 99px; font-size: .78rem; font-weight: 600; background: #f1f5f9; color: #94a3b8; }

        input[type="file"] { display: none; }

        @media (max-width: 768px) {
          .controls-grid { grid-template-columns: 1fr; }
          .controls-left { grid-template-columns: 1fr; }
          .controls-right { flex-wrap: wrap; }
        }
      `}</style>

      <div style={{ maxWidth: '1700px', margin: '0 auto' }}>

        {/* ── Header ── */}
        <div style={{ marginBottom: '1.75rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: 52, height: 52, flexShrink: 0,
            background: 'linear-gradient(135deg,#185FA5,#0C447C)',
            borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(24,95,165,.25)',
          }}>
            <Users size={26} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              Customer Maintenance
            </h1>
            <p style={{ color: '#64748b', fontSize: '.9rem', marginTop: 2 }}>
              Manage marketing customers and BP codes
            </p>
          </div>
        </div>

        {/* ── Controls Card ── */}
        <div className="glass-card" style={{ padding: '1.25rem 1.5rem', marginBottom: '1.25rem' }}>

          {/* Top row */}
          <div className="controls-grid">

            {/* Left: search + filter */}
            <div className="controls-left">
              <div className="icon-wrap">
                <span className="icon-left">
                  <Search size={15} />
                </span>
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="input-field"
                  style={{ paddingLeft: 36 }}
                />
              </div>
              <div className="icon-wrap">
                <span className="icon-left">
                  <Filter size={14} />
                </span>
                <select
                  value={filterField}
                  onChange={e => setFilterField(e.target.value)}
                  className="select-field"
                >
                  <option value="all">All Fields</option>
                  <option value="bpCode">BP Code</option>
                  <option value="bpName">BP Name</option>
                  <option value="groupCode">Group Code</option>
                  <option value="salesEmployeeCode">Sales Employee</option>
                  <option value="taxGroup">Tax Group</option>
                  <option value="shipToDefault">Ship To Default</option>
                </select>
              </div>
            </div>

            {/* Refresh */}
            <button onClick={fetchCustomers} className="btn btn-ghost" disabled={loading}>
              <RefreshCw size={14} style={{ animation: loading ? 'spin .7s linear infinite' : 'none' }} />
              Refresh
            </button>

            {/* Right: action buttons */}
            <div className="controls-right">
              <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} />
              <button onClick={() => fileInputRef.current?.click()} className="btn btn-success">
                <Upload size={14} /> Import
              </button>
              <button onClick={handleExport} className="btn btn-warning">
                <Download size={14} /> Export
              </button>
              <div className="btn-divider" />
              <button onClick={deleteAllCustomers} className="btn btn-danger" disabled={loading || customers.length === 0}>
                <Trash2 size={14} /> Delete All
              </button>
              <button onClick={openCreateModal} className="btn btn-primary">
                <Plus size={14} /> Add New
              </button>
            </div>
          </div>

          {/* Footer badges */}
          <div style={{ display: 'flex', gap: 8, marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
            <span className="stat-badge">
              <span className="stat-dot" style={{ background: '#185FA5' }} />
              Total: {customers.length}
            </span>
            <span className="stat-badge">
              <span className="stat-dot" style={{ background: '#1D9E75' }} />
              Filtered: {filtered.length}
            </span>
            <span className="stat-badge">
              <span className="stat-dot" style={{ background: '#BA7517' }} />
              Page {currentPage} of {totalPages || 1}
            </span>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="glass-card" style={{ marginBottom: '1.25rem' }}>
          <div className="table-container">
            {loading && paginated.length === 0 ? (
              <div style={{ padding: '4rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                <div className="spinner" style={{ width: 28, height: 28 }} />
                <p style={{ color: '#64748b', fontSize: '.9rem' }}>Loading customers...</p>
              </div>
            ) : paginated.length === 0 ? (
              <div style={{ padding: '4rem', textAlign: 'center' }}>
                <p style={{ color: '#94a3b8', fontSize: '1rem' }}>
                  {searchTerm ? 'No customers match your search.' : 'No customers yet. Click "Add New" to get started.'}
                </p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    {[
                      { label: 'ID',             field: 'id' },
                      { label: 'BP Code',        field: 'bpCode' },
                      { label: 'BP Name',        field: 'bpName' },
                      { label: 'Group Code',     field: 'groupCode' },
                      { label: 'Sales Employee', field: 'salesEmployeeCode' },
                      { label: 'Tax Group',      field: 'taxGroup' },
                      { label: 'Withholding',    field: 'subjectToWithholding' },
                      { label: 'Ship To Default',field: 'shipToDefault' },
                    ].map(col => (
                      <th key={col.field} onClick={() => handleSort(col.field)}>
                        <div className="th-inner">
                          {col.label} <SortIcon field={col.field} />
                        </div>
                      </th>
                    ))}
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600, color: '#185FA5', fontSize: '.8rem' }}>#{c.id}</td>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{c.bpCode}</td>
                      <td>{c.bpName || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td>{c.groupCode || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td>{c.salesEmployeeCode || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td>{c.taxGroup || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td>
                        <span className={c.subjectToWithholding ? 'badge-yes' : 'badge-no'}>
                          {c.subjectToWithholding ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>{c.shipToDefault || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          <button onClick={() => openEditModal(c)} className="icon-btn edit" title="Edit">
                            <Edit2 size={15} />
                          </button>
                          <button onClick={() => deleteCustomer(c.id)} className="icon-btn delete" title="Delete">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="glass-card" style={{ padding: '1rem' }}>
            <div className="pagination">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="page-btn">First</button>
              <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="page-btn">‹</button>
              {[...Array(totalPages)].map((_, i) => {
                const p = i + 1;
                if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1))
                  return <button key={p} onClick={() => setCurrentPage(p)} className={`page-btn ${currentPage === p ? 'active' : ''}`}>{p}</button>;
                if (p === currentPage - 2 || p === currentPage + 2)
                  return <span key={p} className="page-ellipsis">···</span>;
                return null;
              })}
              <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="page-btn">›</button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="page-btn">Last</button>
            </div>
          </div>
        )}

        {/* ── Add / Edit Modal ── */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content glass-card" onClick={e => e.stopPropagation()}>
              <div style={{ padding: '1.5rem 1.75rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                  {editingItem ? 'Edit Customer' : 'Add New Customer'}
                </h2>
                <button onClick={closeModal} className="icon-btn" style={{ color: '#94a3b8' }}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={editingItem ? updateCustomer : createCustomer}>
                <div style={{ padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">BP Code <span style={{ color: '#E24B4A' }}>*</span></label>
                    <input name="bpCode" type="text" value={formData.bpCode} onChange={handleInputChange} className="input-field" required placeholder="e.g. C-001" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">BP Name</label>
                    <input name="bpName" type="text" value={formData.bpName} onChange={handleInputChange} className="input-field" placeholder="Business partner name" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label">Group Code</label>
                      <input name="groupCode" type="text" value={formData.groupCode} onChange={handleInputChange} className="input-field" placeholder="Group code" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Sales Employee Code</label>
                      <input name="salesEmployeeCode" type="text" value={formData.salesEmployeeCode} onChange={handleInputChange} className="input-field" placeholder="Sales employee code" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Tax Group</label>
                      <input name="taxGroup" type="text" value={formData.taxGroup} onChange={handleInputChange} className="input-field" placeholder="Tax group" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Ship To Default</label>
                      <input name="shipToDefault" type="text" value={formData.shipToDefault} onChange={handleInputChange} className="input-field" placeholder="Default ship-to address" />
                    </div>
                  </div>
                  <div className="checkbox-row">
                    <input type="checkbox" id="subjectToWithholding" name="subjectToWithholding" checked={formData.subjectToWithholding} onChange={handleInputChange} />
                    <label htmlFor="subjectToWithholding" style={{ margin: 0, cursor: 'pointer', fontSize: '.875rem', color: '#475569', fontWeight: 500 }}>
                      Subject to Withholding
                    </label>
                  </div>
                </div>
                <div style={{ padding: '1rem 1.75rem', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={closeModal} className="btn btn-ghost">Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading
                      ? <><div className="spinner" style={{ width: 14, height: 14 }} />{editingItem ? 'Updating...' : 'Creating...'}</>
                      : editingItem ? 'Update Customer' : 'Create Customer'
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Import Modal ── */}
        {showImportModal && (
          <div className="modal-overlay" onClick={closeImportModal}>
            <div className="modal-content-lg glass-card" onClick={e => e.stopPropagation()}>
              <div style={{ padding: '1.5rem 1.75rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>Import Customers</h2>
                <button onClick={closeImportModal} className="icon-btn" style={{ color: '#94a3b8' }}><X size={20} /></button>
              </div>

              <div style={{ padding: '1.5rem 1.75rem' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                  <span className="stat-badge"><span className="stat-dot" style={{ background: '#1D9E75' }} />New: {importData.length}</span>
                  <span className="stat-badge"><span className="stat-dot" style={{ background: '#BA7517' }} />Duplicates: {duplicates.length}</span>
                </div>

                {duplicates.length > 0 && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '1rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', marginBottom: '1rem', color: '#92400e' }}>
                      <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <strong style={{ fontSize: '.9rem' }}>Duplicate BP Codes found!</strong>
                        <p style={{ fontSize: '.84rem', marginTop: 4, color: '#78350f' }}>
                          Choose what to do for each — <strong>Skip</strong> to keep existing, or <strong>Update</strong> to overwrite with import data.
                        </p>
                      </div>
                    </div>
                    <div style={{ maxHeight: '340px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {duplicates.map((dup, i) => (
                        <div key={i} className="dup-card">
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '10px', fontSize: '.84rem' }}>
                            <div>
                              <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Existing</div>
                              <div style={{ color: '#475569', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span><b>Code:</b> {dup.existing.bpCode}</span>
                                <span><b>Name:</b> {dup.existing.bpName || '—'}</span>
                                <span><b>Group:</b> {dup.existing.groupCode || '—'}</span>
                                <span><b>Sales:</b> {dup.existing.salesEmployeeCode || '—'}</span>
                              </div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: 4 }}>Import Data</div>
                              <div style={{ color: '#475569', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span><b>Code:</b> {dup.bpCode}</span>
                                <span><b>Name:</b> {dup.bpName || '—'}</span>
                                <span><b>Group:</b> {dup.groupCode || '—'}</span>
                                <span><b>Sales:</b> {dup.salesEmployeeCode || '—'}</span>
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className={`act-btn act-skip ${dup.action === 'skip' ? 'on' : ''}`} onClick={() => handleDuplicateAction(i, 'skip')}>⏭ Skip</button>
                            <button className={`act-btn act-update ${dup.action === 'update' ? 'on' : ''}`} onClick={() => handleDuplicateAction(i, 'update')}>🔄 Update</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {importData.length > 0 && (
                  <div>
                    <p style={{ fontSize: '.9rem', fontWeight: 600, color: '#0f172a', marginBottom: '10px' }}>
                      New customers to import ({importData.length})
                    </p>
                    <div style={{ maxHeight: '240px', overflowY: 'auto', background: '#f8fafc', padding: '10px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {importData.slice(0, 10).map((item, i) => (
                        <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', fontSize: '.84rem' }}>
                          <div style={{ fontWeight: 600, color: '#185FA5' }}>{item.bpCode}</div>
                          <div style={{ color: '#475569', marginTop: 2 }}>{item.bpName || '—'}</div>
                          <div style={{ display: 'flex', gap: '12px', marginTop: 4, color: '#94a3b8', fontSize: '.78rem' }}>
                            <span>Group: {item.groupCode || '—'}</span>
                            <span>Sales: {item.salesEmployeeCode || '—'}</span>
                            <span>Tax: {item.taxGroup || '—'}</span>
                          </div>
                        </div>
                      ))}
                      {importData.length > 10 && (
                        <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '.84rem', padding: '6px 0' }}>
                          +{importData.length - 10} more records
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ padding: '1rem 1.75rem', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={closeImportModal} className="btn btn-ghost">Cancel</button>
                <button
                  onClick={processImport}
                  className="btn btn-primary"
                  disabled={loading || (importData.length === 0 && duplicates.every(d => d.action === 'skip'))}
                >
                  {loading
                    ? <><div className="spinner" style={{ width: 14, height: 14 }} />Processing...</>
                    : `Import ${importData.length + duplicates.filter(d => d.action === 'update').length} Customers`
                  }
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CustomerMaintenance;